import React, { useId } from 'react';
import { Img, Sequence, useCurrentFrame, useVideoConfig } from 'remotion';
import { Video } from '@remotion/media';
import { CircleHelp, icons } from 'lucide-react';
import { evaluateMotionElement, evaluateMotionParticles, expandMotionRepeat } from '@/lib/motion';
import type { MotionElement, MotionScene } from '@/lib/motion';
import { designScale } from '@/lib/render/designScale';
import { MotionFonts } from './MotionFonts';
import { MotionShape, MotionShapeGeometry } from './MotionShape';
import { MotionText, MotionCaptions } from './MotionText';
import { motionBoxStyle, paintToCss } from './renderStyle';

type Canvas = { width: number; height: number; fps: number };
function MotionIcon({ element }: { element: MotionElement }) {
  const name = (element.name ?? '').replace(/(^|-)(\w)/g, (_, _prefix, letter: string) => letter.toUpperCase());
  const Icon = icons[name as keyof typeof icons] ?? CircleHelp;
  return <Icon width={element.width ?? 64} height={element.height ?? 64} color={element.color ?? '#ffffff'} strokeWidth={element.strokeWidth ?? 2} />;
}

function Particles({ element, time }: { element: MotionElement; time: number }) {
  return <>{evaluateMotionParticles({ ...element, x: 0, y: 0 }, time).map((particle) => <div key={particle.id}
    data-motion-particle={particle.id} style={{ position: 'absolute', left: particle.x, top: particle.y,
      width: particle.size, height: particle.size, opacity: particle.opacity,
      transform: `translate(-50%, -50%) rotate(${particle.rotation}deg)`, color: particle.color,
      fontSize: particle.size, lineHeight: 1 }}>
    {particle.shape === 'image' && particle.src ? <Img src={particle.src} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
      : particle.shape === 'icon' ? <MotionIcon element={{ ...element, name: particle.name, width: particle.size, height: particle.size, color: particle.color }} />
      : particle.shape === 'emoji' ? particle.emoji
      : <div style={{ width: '100%', height: '100%', background: particle.color, borderRadius: particle.shape === 'circle' ? '50%' : 0 }} />}
  </div>)}</>;
}

function Group({ authored, evaluated, time, canvas }: { authored: MotionElement; evaluated: MotionElement; time: number; canvas: Canvas }) {
  const clipId = `motion-clip-${useId().replace(/:/g, '')}`;
  const shapeId = typeof evaluated.clip === 'object' ? evaluated.clip.shape : undefined;
  const shape = shapeId ? authored.children?.find((child) => child.id === shapeId) : undefined;
  const mask = shape ? evaluateMotionElement(shape, time) : null;
  return <div data-motion-id={authored.id} style={{ ...motionBoxStyle(evaluated),
    width: evaluated.width ?? canvas.width, height: evaluated.height ?? canvas.height,
    clipPath: mask ? `url(#${clipId})` : motionBoxStyle(evaluated).clipPath }}>
    {mask && <svg width={0} height={0} style={{ position: 'absolute' }}><defs><clipPath id={clipId} clipPathUnits="userSpaceOnUse">
      <g transform={`translate(${mask.x ?? 0}, ${mask.y ?? 0}) rotate(${mask.rotation ?? 0}) scale(${(mask.scale ?? 1) * (mask.scaleX ?? 1)}, ${(mask.scale ?? 1) * (mask.scaleY ?? 1)}) skewX(${mask.skewX ?? 0}) skewY(${mask.skewY ?? 0}) translate(${-(mask.anchor?.x ?? 0) * (mask.width ?? 100)}, ${-(mask.anchor?.y ?? 0) * (mask.height ?? 100)})`}>
        <MotionShapeGeometry element={mask} mask />
      </g>
    </clipPath></defs></svg>}
    {authored.children?.map((child) => <ElementCopies key={child.id} element={child} time={time} canvas={canvas} />)}
  </div>;
}

function Element({ element, time, canvas }: { element: MotionElement; time: number; canvas: Canvas }) {
  // Off-screen elements skip evaluation (it copies the element every frame).
  if (time < element.start || time >= element.end) return null;
  // Split text applies staggered enter/exit to the individual spans rather than twice.
  const isSplit = element.type === 'text' && element.split && element.split !== 'none';
  const evaluated = evaluateMotionElement(isSplit ? { ...element,
    enter: element.enter?.type === 'typewriter' ? element.enter : undefined,
    exit: element.exit?.type === 'typewriter' ? element.exit : undefined } : element, time);
  if (element.type === 'group') return <Group authored={element} evaluated={evaluated} time={time} canvas={canvas} />;
  const style = motionBoxStyle(evaluated);
  let content: React.ReactNode;
  if (element.type === 'text') content = <MotionText authored={element} evaluated={evaluated} time={time} />;
  else if (element.type === 'captions') content = <MotionCaptions authored={element} evaluated={evaluated} time={time} />;
  else if (element.type === 'icon') content = <MotionIcon element={evaluated} />;
  else if (element.type === 'particles') content = <Particles element={{ ...evaluated, opacity: 1 }} time={time} />;
  else if (element.type === 'image') content = <Img src={evaluated.src!} style={{ width: '100%', height: '100%', objectFit: evaluated.fit ?? 'cover', borderRadius: evaluated.radius }} />;
  else if (element.type === 'video') content = <Sequence from={Math.round(element.start * canvas.fps)} durationInFrames={Math.max(1, Math.round((element.end - element.start) * canvas.fps))} layout="none">
    <Video src={evaluated.src!} muted={evaluated.muted ?? true} playbackRate={element.speed ?? 1}
      trimBefore={Math.round((evaluated.trimStart ?? 0) * canvas.fps)} loop={element.loop ?? false}
      objectFit={evaluated.fit ?? 'cover'} style={{ width: '100%', height: '100%' }} />
  </Sequence>;
  else content = <MotionShape element={evaluated} canvasWidth={canvas.width} canvasHeight={canvas.height} />;
  return <div data-motion-id={element.id} style={{ ...style,
    ...(['image', 'video'].includes(element.type) ? { width: evaluated.width ?? canvas.width, height: evaluated.height ?? canvas.height } : {}) }}>{content}</div>;
}

function ElementCopies({ element, time, canvas }: { element: MotionElement; time: number; canvas: Canvas }) {
  return <>{expandMotionRepeat(element).map((copy) => <Element key={copy.id} element={copy} time={time} canvas={canvas} />)}</>;
}

/** One layer-local clock, one design scale, shared by Player, browser stills and exports. */
export function MotionSceneRenderer({ scene }: { scene: MotionScene }) {
  const frame = useCurrentFrame();
  const { width, height, fps } = useVideoConfig();
  const scale = designScale(width, height);
  const canvas = { width: width / scale, height: height / scale, fps };
  return <div data-testid="motion-design-canvas" style={{ position: 'absolute', left: 0, top: 0,
    width: canvas.width, height: canvas.height, transform: `scale(${scale})`, transformOrigin: '0 0',
    background: paintToCss(scene.background), pointerEvents: 'none' }}>
    <MotionFonts scene={scene} />
    {scene.elements.map((element) => <ElementCopies key={element.id} element={element} time={frame / fps} canvas={canvas} />)}
  </div>;
}
